import React, { useState } from 'react';
import './Feedback.scss';

export const FEEDBACK_API = '/api/feedback.php';

const QUALITY = ['Very Poor', 'Poor', 'Average', 'Good', 'Excellent'];

const STAR_QUESTIONS = [
  { key: 'overall', label: 'How would you rate your overall experience?', labels: QUALITY },
  { key: 'food', label: 'How would you rate the quality of our food?', labels: QUALITY },
  {
    key: 'taste',
    label: 'How satisfied were you with the taste and presentation of the food?',
    labels: ['Very Dissatisfied', 'Dissatisfied', 'Neutral', 'Satisfied', 'Very Satisfied'],
  },
  { key: 'service', label: 'How would you rate our staff and customer service?', labels: QUALITY },
  { key: 'cleanliness', label: 'How would you rate the cleanliness and atmosphere of the restaurant?', labels: QUALITY },
  { key: 'ambiance', label: 'How would you rate the restaurant’s ambiance and traditional Haveli experience?', labels: QUALITY },
];

const ON_TIME_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'improve', label: 'Could be improved' },
];

const CHOICE_QUESTIONS = [
  {
    key: 'recommend',
    label: 'Would you recommend Haveli Restaurant to your friends and family?',
    labels: ['Definitely No', 'Probably No', 'Not Sure', 'Probably Yes', 'Definitely Yes'],
  },
  {
    key: 'visit_again',
    label: 'How likely are you to visit Haveli Restaurant again?',
    labels: ['Very Unlikely', 'Unlikely', 'Maybe', 'Likely', 'Very Likely'],
  },
];

const REQUIRED = [...STAR_QUESTIONS.map((q) => q.key), 'on_time', 'value', ...CHOICE_QUESTIONS.map((q) => q.key)];

const StarQuestion = ({ number, label, labels, value, onChange, missing }) => (
  <fieldset className={`fb-question ${missing ? 'fb-question--missing' : ''}`}>
    <legend>
      <span className="fb-num">{number}.</span> {label}
    </legend>
    <div className="fb-stars" role="radiogroup">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          role="radio"
          aria-checked={value === n}
          aria-label={labels[n - 1]}
          className={value >= n ? 'is-on' : ''}
          onClick={() => onChange(n)}
        >
          ★
        </button>
      ))}
    </div>
    <div className="fb-star-label">{value ? labels[value - 1] : 'Tap a star'}</div>
  </fieldset>
);

const ChoiceQuestion = ({ number, label, options, value, onChange, missing }) => (
  <fieldset className={`fb-question ${missing ? 'fb-question--missing' : ''}`}>
    <legend>
      <span className="fb-num">{number}.</span> {label}
    </legend>
    <div className="fb-choices" role="radiogroup">
      {options.map((opt) => (
        <button
          type="button"
          key={opt.value}
          role="radio"
          aria-checked={value === opt.value}
          className={value === opt.value ? 'is-on' : ''}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </fieldset>
);

const TextQuestion = ({ number, label, value, onChange }) => (
  <fieldset className="fb-question">
    <legend>
      <span className="fb-num">{number}.</span> {label} <span className="fb-optional">(optional)</span>
    </legend>
    <textarea rows={3} maxLength={1000} value={value} onChange={(e) => onChange(e.target.value)} />
  </fieldset>
);

const toOptions = (labels) => labels.map((label, i) => ({ value: i + 1, label })).reverse();

const Feedback = () => {
  const [answers, setAnswers] = useState({ enjoyed: '', improve: '', comments: '' });
  const [showMissing, setShowMissing] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | sending | done
  const [error, setError] = useState('');

  const set = (key) => (value) => setAnswers((a) => ({ ...a, [key]: value }));
  const missing = REQUIRED.filter((key) => answers[key] === undefined);

  const submit = async (e) => {
    e.preventDefault();
    if (missing.length) {
      setShowMissing(true);
      setError('Please answer all the questions marked in red.');
      document.querySelector('.fb-question--missing')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setStatus('sending');
    setError('');
    try {
      const res = await fetch(FEEDBACK_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(answers),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Something went wrong. Please try again.');
      setStatus('done');
      window.scrollTo(0, 0);
    } catch (err) {
      setStatus('idle');
      setError(err.message);
    }
  };

  const isMissing = (key) => showMissing && answers[key] === undefined;

  return (
    <div className="fb-page">
      <header className="fb-header">
        <img src="/haveli-logo.png" alt="Haveli" />
        <div>
          <h1>Haveli Restaurant</h1>
          <p>The Royal Experience</p>
        </div>
      </header>

      {status === 'done' ? (
        <section className="fb-card fb-thanks">
          <div className="fb-thanks__icon">★</div>
          <h2>Thank you for your valuable feedback!</h2>
          <p>Your suggestions help us improve and provide you with an even better experience.</p>
          <a href="/" className="fb-submit">Visit our website</a>
        </section>
      ) : (
        <form className="fb-card" onSubmit={submit} noValidate>
          <h2>Customer Feedback</h2>
          <p className="fb-intro">
            Thank you for dining at Haveli Restaurant. We value your feedback and would love to hear about your experience.
          </p>

          {STAR_QUESTIONS.map((q, i) => (
            <StarQuestion
              key={q.key}
              number={i + 1}
              label={q.label}
              labels={q.labels}
              value={answers[q.key]}
              onChange={set(q.key)}
              missing={isMissing(q.key)}
            />
          ))}

          <ChoiceQuestion
            number={7}
            label="Was your order served within a reasonable time?"
            options={ON_TIME_OPTIONS}
            value={answers.on_time}
            onChange={set('on_time')}
            missing={isMissing('on_time')}
          />

          <StarQuestion
            number={8}
            label="How would you rate the value for money?"
            labels={QUALITY}
            value={answers.value}
            onChange={set('value')}
            missing={isMissing('value')}
          />

          <TextQuestion number={9} label="What did you enjoy most about your visit to Haveli Restaurant?" value={answers.enjoyed} onChange={set('enjoyed')} />
          <TextQuestion number={10} label="Was there anything that you think we could improve?" value={answers.improve} onChange={set('improve')} />

          {CHOICE_QUESTIONS.map((q, i) => (
            <ChoiceQuestion
              key={q.key}
              number={11 + i}
              label={q.label}
              options={toOptions(q.labels)}
              value={answers[q.key]}
              onChange={set(q.key)}
              missing={isMissing(q.key)}
            />
          ))}

          <TextQuestion number={13} label="Any additional comments or suggestions?" value={answers.comments} onChange={set('comments')} />

          {error && <p className="fb-error">{error}</p>}

          <button type="submit" className="fb-submit" disabled={status === 'sending'}>
            {status === 'sending' ? 'Submitting…' : 'Submit Feedback'}
          </button>
        </form>
      )}
    </div>
  );
};

export default Feedback;
